/**
 * Guard against third-party DOM rewriters crashing React's commit phase.
 *
 * Google Translate (and Grammarly, some ad blockers, etc.) mutates the live
 * DOM behind React's back — most notoriously, in-page translation wraps every
 * text node in `<font style="vertical-align: inherit;">` elements. React's
 * reconciler still holds references to the original nodes, so its next commit
 * calls `parent.removeChild(child)` / `parent.insertBefore(node, ref)` against
 * a parent that no longer owns them, and the DOM throws:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * That single throw unwinds the whole render into the ErrorBoundary — a
 * translated student chatting with Ka (streaming re-renders ~20×/s) hits it
 * almost immediately.
 *
 * The fix (the long-standing workaround from facebook/react#11538, used in
 * production by github.com among others): make the two methods tolerant of
 * exactly the already-broken case. Behavior is unchanged whenever the DOM is
 * consistent — we only diverge where the native call would have thrown:
 *
 *  - `removeChild` of a node that is no longer ours → no-op, return the node.
 *  - `insertBefore` with a reference node that is no longer ours → append
 *    instead (the reference position no longer exists).
 *
 * The worst case is a stray stale/translated text fragment on screen until
 * the next full re-render — strictly better than a crash page. Translation
 * itself keeps working; we deliberately do NOT use `translate="no"` here,
 * since students using Google Translate rely on it.
 */

let installed = false;
let warnedRemove = false;
let warnedInsert = false;

export function installDomMutationGuard(): void {
  if (installed || typeof Node === 'undefined') return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      if (!warnedRemove) {
        warnedRemove = true;
        console.warn(
          '[dom-mutation-guard] Ignored removeChild for a node that is no ' +
            'longer a child of its React parent (DOM was modified externally, ' +
            'e.g. by Google Translate).',
        );
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode !== null && referenceNode.parentNode !== this) {
      if (!warnedInsert) {
        warnedInsert = true;
        console.warn(
          '[dom-mutation-guard] insertBefore reference node is no longer a ' +
            'child of its React parent (DOM was modified externally, e.g. by ' +
            'Google Translate) — appending instead.',
        );
      }
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
